import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Input, Prompt, showNotification } from 'sonata-react-components';
import { Venue } from 'sonata-static-storage/lib/schemas/venue/venue.schema';

import { getUserId } from '@/helpers/user/user.helpers';
import { putUser } from '@/services/user.service';
import { updateVenue } from '@/services/venue.service';
import { reloadUser } from '@/store/global/user/selectors';
import { generateVenueGroup } from './helpers';

import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';

import './groupVenues.scss';

type Props = {
  onConfirm: () => void;
  onCancel: () => void;
  user: UserInterface;
  venues: Venue[];
};

type State = {
  isGrouping: boolean;
  groupName: string | null;
  isValidGroupName: boolean;
};

export function GroupVenues({ onCancel, onConfirm, user, venues }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const [{ isGrouping, groupName, isValidGroupName }, setState] = useState<State>({
    groupName: null,
    isGrouping: false,
    isValidGroupName: true
  });

  const userId = getUserId(user);
  const BASE_ID = 'groupVenuesPrompt';

  if (venues.length === 0) {
    return null;
  }

  const handleNameChange = (name: string) => {
    setState(prev => ({
      ...prev,
      groupName: name ?? null
    }));
  };

  const tokens = {
    reloadUser: {},
    user: {},
    venue: {}
  };
  const handleConfirm = async () => {
    if (isGrouping) {
      return;
    }
    const isValidGroupName = Boolean(groupName && groupName?.length >= 1);
    if (!isValidGroupName) {
      setState(prev => ({
        ...prev,
        isValidGroupName: false
      }));

      return;
    }
    try {
      setState(prev => ({ ...prev, isGrouping: true }));
      const venueGroup = user.venueGroups.find(vg => vg.name === groupName);
      const group = venueGroup ? venueGroup : generateVenueGroup(groupName as string);
      if (!venueGroup) {
        const userWithVenueGroup: UserInterface = {
          ...user,
          venueGroups: [...user.venueGroups, group]
        };
        await putUser(userWithVenueGroup, tokens.user);
        reloadUser(tokens.reloadUser);
      }
      await venues.reduce(async (prev, venue) => {
        await prev;
        venue.groups = [group._id as string];
        await updateVenue(venue, userId, tokens.venue);
      }, Promise.resolve());
      onConfirm();
    } catch (_) {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'An error occurred while grouping the venues. Please try again.',
          id: 'menu.myVenues.group.error'
        })
      );
    } finally {
      setState(prev => ({ ...prev, isGrouping: false }));
    }
  };

  return (
    <Prompt
      buttons={[
        {
          id: `${BASE_ID}_cancelBtn`,
          key: `${BASE_ID}_cancel`,
          onClick: onCancel,
          text: formatMessage({
            defaultMessage: 'Cancel',
            id: 'common.cancel'
          }),
          type: 'secondary'
        },
        {
          disabled: !isValidGroupName,
          id: `${BASE_ID}_cofirmBtn`,
          key: `${BASE_ID}_confirm`,
          loading: isGrouping,
          onClick: handleConfirm,
          text: formatMessage({
            defaultMessage: 'Confirm',
            id: 'common.confirm'
          })
        }
      ]}
      className="groupVenuesPrompt"
      closePrompt={onCancel}
      id={`${BASE_ID}_prompt`}
      title={formatMessage({
        defaultMessage: 'Are you sure you want to group these venues?',
        id: 'menu.myVenues.group.title'
      })}
    >
      <h5 className="groupVenuesPrompt__description">
        {formatMessage({
          defaultMessage: 'Venues selected for grouping',
          id: 'menu.myVenues.group.description'
        })}
      </h5>
      <ul className="groupVenuesPrompt__list">
        {venues.map(venue => (
          <li className="groupVenuesPrompt__listItem" key={venue._id}>
            <span className="groupVenuesPrompt__listItemName">{venue.name}</span>
            <span className="groupVenuesPrompt__listItemAddress">{venue.address || 'N/A'}</span>
          </li>
        ))}
      </ul>
      <Input
        className="groupVenuesPrompt__input"
        id={`${BASE_ID}_groupNameInput`}
        inputStatus={isValidGroupName ? 'default' : 'wrong'}
        label={formatMessage({
          defaultMessage: 'Group name',
          id: 'menu.myVenues.group.inputLabel'
        })}
        name="venuesGroupName"
        notFloatingPlaceholder
        onChange={value => handleNameChange(value as string)}
        placeholder={formatMessage({
          defaultMessage: 'Type a name for your group',
          id: 'menu.myVenues.group.inputPlaceholder'
        })}
        shape="squareUI"
        type="email"
        value={groupName}
      />
    </Prompt>
  );
}
